import React, { useMemo, useState } from 'react';
import { Alert, Tooltip } from 'antd';
import { Accordion, Card, Select, SelectOption } from '../../design-system';
import {
  AutoParameterRecommendation,
  SelectedFziFunctionsOptions
} from '../../@types/projects/types';
import { useAutoCorrelation } from '../../providers/AutoCorrelation';
import {
  PARAMETER_FAMILIES,
  TENDENCY_LABELS,
  candidateWarnings,
  flagMessages,
  isRejectedCandidate,
  parameterLabel
} from './autoCorrelationUtils';
import CombinationPreview from './CombinationPreview';
import styles from './AutoCorrelation.module.css';

const formatNumber = (value: number | null): string =>
  value === null || !Number.isFinite(value) ? '—' : value.toFixed(3);

const ParameterRow = ({
  parameter,
  showRejected
}: {
  parameter: AutoParameterRecommendation;
  showRejected: boolean;
}) => {
  const { stagedSelection, overrides, setOverride } = useAutoCorrelation();

  const staged = stagedSelection[parameter.parameter];
  const isOverridden = Boolean(overrides[parameter.parameter]);

  const selectOptions: SelectOption[] = useMemo(
    () =>
      parameter.candidates
        .filter(candidate => showRejected || !isRejectedCandidate(candidate))
        .map(candidate => {
          const rejected = isRejectedCandidate(candidate);
          const markers = [
            candidate.tendency_type === parameter.recommended
              ? ' (recommended)'
              : '',
            candidate.tendency_type === parameter.best_r_squared
              ? ' (best R²)'
              : '',
            rejected ? ' — rejected' : ''
          ].join('');
          return {
            value: candidate.tendency_type,
            label: `${TENDENCY_LABELS[candidate.tendency_type]}${markers}`,
            disabled: rejected
          };
        }),
    [parameter, showRejected]
  );

  const handleOverrideChange = (value: string) => {
    const type = value as SelectedFziFunctionsOptions;
    // Choosing the recommendation back clears the override.
    setOverride(
      parameter.parameter,
      type === parameter.recommended ? null : type
    );
  };

  return (
    <div
      className={styles.parameterBlock}
      data-testid={`auto-parameter-${parameter.parameter}`}
    >
      <div className={styles.parameterHeader}>
        <span className={styles.parameterName}>
          {parameterLabel(parameter.parameter)}
        </span>
        <span className={styles.parameterMeta}>
          {parameter.sample_count} sample
          {parameter.sample_count === 1 ? '' : 's'}
        </span>
        {parameter.flags.length > 0 ? (
          <Tooltip title={flagMessages(parameter.flags)}>
            <span className={styles.flagIconWarning}>
              ⚠ {parameter.flags.length}
            </span>
          </Tooltip>
        ) : null}
        <div style={{ minWidth: '220px', marginLeft: 'auto' }}>
          <Select
            options={selectOptions}
            value={staged}
            onChange={handleOverrideChange}
            placeholder="Function"
            size="sm"
          />
        </div>
        {isOverridden ? (
          <span className={`${styles.marker} ${styles.markerBestR2}`}>
            Overridden
          </span>
        ) : null}
      </div>

      <div className={styles.candidateTableWrapper}>
        <table className={styles.candidateTable}>
          <thead>
            <tr>
              <th>Fit</th>
              {parameter.candidates.map(candidate => {
                const rejected = isRejectedCandidate(candidate);
                return (
                  <th
                    key={candidate.tendency_type}
                    className={
                      candidate.tendency_type === staged
                        ? styles.stagedColumn
                        : rejected
                        ? styles.rejectedCell
                        : undefined
                    }
                  >
                    {TENDENCY_LABELS[candidate.tendency_type]}
                    {candidate.tendency_type === parameter.recommended ? (
                      <span
                        className={`${styles.marker} ${styles.markerRecommended}`}
                      >
                        Recommended
                      </span>
                    ) : null}
                    {candidate.tendency_type === parameter.best_r_squared ? (
                      <span
                        className={`${styles.marker} ${styles.markerBestR2}`}
                      >
                        Best R²
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Score</td>
              {parameter.candidates.map(candidate => (
                <td
                  key={candidate.tendency_type}
                  className={
                    isRejectedCandidate(candidate)
                      ? styles.rejectedCell
                      : undefined
                  }
                >
                  {formatNumber(candidate.score)}
                </td>
              ))}
            </tr>
            <tr>
              <td>R²</td>
              {parameter.candidates.map(candidate => (
                <td
                  key={candidate.tendency_type}
                  className={
                    isRejectedCandidate(candidate)
                      ? styles.rejectedCell
                      : undefined
                  }
                >
                  {formatNumber(candidate.r_squared)}
                </td>
              ))}
            </tr>
            <tr>
              <td>Flags</td>
              {parameter.candidates.map(candidate => {
                const rejects = candidate.flags.filter(
                  flag => flag.severity === 'reject'
                );
                const warnings = candidateWarnings(candidate);
                return (
                  <td key={candidate.tendency_type}>
                    {rejects.length > 0 ? (
                      <Tooltip title={flagMessages(rejects)}>
                        <span className={styles.flagIconReject}>
                          ✕ {rejects.length}
                        </span>
                      </Tooltip>
                    ) : null}{' '}
                    {warnings.length > 0 ? (
                      <Tooltip title={flagMessages(warnings)}>
                        <span className={styles.flagIconWarning}>
                          ⚠ {warnings.length}
                        </span>
                      </Tooltip>
                    ) : null}
                    {rejects.length === 0 && warnings.length === 0 ? '—' : null}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Step 3 — review the automatic recommendation: per-family parameter
 * candidates (with overrides) and the combination comparison with curve
 * previews.
 */
const ReviewStep = () => {
  const { report } = useAutoCorrelation();
  const [showRejected, setShowRejected] = useState(false);

  const families = useMemo(() => {
    if (!report) return [];
    return PARAMETER_FAMILIES.map(family => ({
      ...family,
      recommendations: family.parameters
        .map(parameter => report.parameters[parameter])
        .filter(Boolean),
      warnings: report.flags.filter(
        flag => flag.context?.family === family.key
      ),
      availability: report.scope.family_availability?.[family.key]
    })).filter(
      family =>
        family.recommendations.length > 0 ||
        family.warnings.length > 0 ||
        Boolean(family.availability?.requested_count)
    );
  }, [report]);

  if (!report) return null;

  const reportWarnings = report.flags.filter(
    flag => flag.severity !== 'info'
  );

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Recommended functions</h3>
        <p className={styles.sectionSubtitle}>
          These are suggested correlations — review them and override any
          function below before applying.
        </p>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: '0.75rem' }}
        message={`Kr source: ${(report.scope.endpoint_source ?? 'lab') === 'refinement' ? 'Refinement values' : 'Lab values'}`}
        description="Liao and Brooks-Corey combinations use this same Water-Oil and Gas-Oil Kr source."
      />

      {reportWarnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: '0.75rem' }}
          message="Report warnings"
          description={
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {reportWarnings.map(flag => (
                <li key={flag.code}>{flag.message}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      <div className={styles.reviewToolbar}>
        <span className={styles.reviewMeta}>
          Scoring {report.scoring_version} · x: {report.x_variable.toUpperCase()}
        </span>
        <label className={styles.showRejectedToggle}>
          <input
            type="checkbox"
            checked={showRejected}
            onChange={event => setShowRejected(event.target.checked)}
            data-testid="auto-show-rejected"
          />
          Show rejected candidates in the override list
        </label>
      </div>

      <Card variant="outlined" padding="sm" fullWidth className={styles.section}>
        <Accordion multiple defaultOpen={families[0]?.key}>
          {families.map(family => (
            <Accordion.Item
              key={family.key}
              id={family.key}
              header={
                <span>
                  {family.label}
                  {family.availability ? (
                    <> · {family.availability.usable_count}/
                      {family.availability.requested_count} used</>
                  ) : null}
                </span>
              }
            >
              {family.availability?.unavailable_samples.length ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: '0.75rem' }}
                  message={`${family.availability.unavailable_samples.length} sample${family.availability.unavailable_samples.length === 1 ? '' : 's'} unavailable`}
                  description={family.availability.unavailable_samples
                    .map(
                      sample =>
                        `${sample.sample_code}: ${sample.missing_fields.join(', ')}`
                    )
                    .join(' · ')}
                />
              ) : null}
              {family.recommendations.length > 0 ? (
                family.recommendations.map(parameter => (
                  <ParameterRow
                    key={parameter.parameter}
                    parameter={parameter}
                    showRejected={showRejected}
                  />
                ))
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message={`No ${family.label} recommendations available`}
                  description={family.warnings.map(flag => flag.message).join(' ')}
                />
              )}
            </Accordion.Item>
          ))}
        </Accordion>
      </Card>

      <CombinationPreview />
    </div>
  );
};

export default ReviewStep;
